import type { PriceSource } from "../../api/client";

export const ALL_PRICE_BY_CATEGORY_TOKEN = "__all_price_by_category__";

export type CategoryOption = {
  key: string;
  label: string;
  itemCount: number;
  available: boolean;
};

export type PriceSection = CategoryOption & { items: PriceSource["items"] };

const normalize = (value: string) => value.trim().toLocaleLowerCase("ru");
const unique = (values: string[]) => [...new Set(values)];

export const isWholePriceByCategory = (keys: string[]) =>
  keys.includes(ALL_PRICE_BY_CATEGORY_TOKEN);

export const categorySelectionKeys = (keys: string[]) => isWholePriceByCategory(keys)
  ? [ALL_PRICE_BY_CATEGORY_TOKEN]
  : unique(keys.filter((key) => key !== ALL_PRICE_BY_CATEGORY_TOKEN));

export const buildCategoryOptions = (
  source: PriceSource,
  selectedKeys: string[] = []
): CategoryOption[] => {
  const counts = new Map<string, number>();
  const sourceOrder = new Map<string, string>();

  source.categories.forEach(({ key, label }) => {
    if (!sourceOrder.has(key)) sourceOrder.set(key, label);
  });
  source.items.forEach((item) => {
    counts.set(item.categoryKey, (counts.get(item.categoryKey) ?? 0) + 1);
    if (!sourceOrder.has(item.categoryKey)) sourceOrder.set(item.categoryKey, item.categoryLabel);
  });

  const available = [...sourceOrder].map(([key, label]) => ({
    key,
    label,
    itemCount: counts.get(key) ?? 0,
    available: true
  }));
  const known = new Set(sourceOrder.keys());
  const missing = unique(selectedKeys)
    .filter((key) => key !== ALL_PRICE_BY_CATEGORY_TOKEN)
    .filter((key) => !known.has(key))
    .map((key) => ({ key, label: key, itemCount: 0, available: false }));

  return [...available, ...missing];
};

export const filterCategoryOptions = (
  options: CategoryOption[],
  query: string
): CategoryOption[] => {
  const term = normalize(query);
  return term ? options.filter((option) => normalize(option.label).includes(term)) : options;
};

export const buildPriceSections = (
  source: PriceSource,
  selectedKeys: string[],
  query: string
): PriceSection[] => {
  const selected = new Set(categorySelectionKeys(selectedKeys));
  const term = normalize(query);

  return buildCategoryOptions(source)
    .filter((option) => selected.size === 0 || selected.has(ALL_PRICE_BY_CATEGORY_TOKEN) || selected.has(option.key))
    .map((option) => ({
      ...option,
      items: source.items
        .filter((item) => item.categoryKey === option.key)
        .filter((item) => !term || [item.name, item.categoryLabel, item.countryCode ?? ""]
          .some((value) => normalize(value).includes(term)))
    }))
    .filter((section) => section.items.length > 0);
};
